import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AvailityVOBAErrorMessages } from '@finpay-development/shared-platform/lib/esm/shared/enums';
import { showErrorStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {
  createSelectedVob,
  loadPatientVobList,
} from '@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk';
import { ClientVobStatusCardViewModel } from '@finpay-development/shared-platform/lib/esm/shared/model/client-vob-status-card';
import { PayerRow } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/payers';
import { AxiosResultStatus } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-result-status';
import { AppDispatch } from '../../../../../shared/state/store';
import { RootState } from '../../../../../shared/state/root-reducer';
import { PatientFormValues } from '../types';
import {
  AVAILITY_FACILITY_MENU_MAX,
  FacilityWithNpi,
  formatAvailityDob,
  getAvailityVobWithOverrides,
  isAvailityRequestCanceled,
  listFacilitiesWithValidNpi,
} from '../utils/availity-vob';
import { calculateCompScore } from '../utils/vob-comp-score';
import {
  buildVerifyPayerCandidates,
  getAvailityPcodeForPayor,
  normalizePayorName,
  VerifyPayerCandidate,
} from '../utils/verify-payer-candidates';

export type VerifyPayerInsuranceOverride = {
  payorId: number;
  payorPlanId: number;
};

export type SavePatientFormResult =
  | { ok: true; patientId: number }
  | { ok: false };

export type SavePatientFormOptions = {
  skipNavigateOnCreate?: boolean;
};

type CandidateRunResult = {
  candidate: VerifyPayerCandidate;
  response: AxiosResultStatus;
  canceled: boolean;
  completed: boolean;
  inactive: boolean;
  compScore: number;
  planId: number;
  planName?: string;
};

type ClientVobVerifyFields = ClientVobStatusCardViewModel & {
  isVerifyEnabled?: boolean;
  verifyPayorIds?: number[] | null;
};

function isAvailityVobConfigVisible(
  clientId: number,
  facilityId: number,
  allClientVobs: ClientVobStatusCardViewModel[] | null | undefined
): boolean {
  return !!findMatchingVerifyClientVob(clientId, facilityId, allClientVobs);
}

/**
 * Same facility/default precedence as Verify visibility — used for configured payor ids.
 */
function findMatchingVerifyClientVob(
  clientId: number,
  facilityId: number,
  allClientVobs: ClientVobStatusCardViewModel[] | null | undefined
): ClientVobVerifyFields | null {
  if (!clientId || !allClientVobs) {
    return null;
  }

  const clientVobsForPatient = allClientVobs.filter(
    (clientVob) => clientVob.clientId === Number(clientId)
  );

  if (facilityId > 0) {
    const facilityExistsInClientVobs = clientVobsForPatient.some(
      (clientVob) => Number(clientVob.clientFacilityId) === facilityId
    );

    if (facilityExistsInClientVobs) {
      return (
        (clientVobsForPatient.find(
          (clientVob) =>
            Number(clientVob.clientFacilityId) === facilityId &&
            clientVob.vobTypeId === 3 &&
            clientVob.isIntEnabled === true &&
            isClientVobVerifyEnabled(clientVob)
        ) as ClientVobVerifyFields | undefined) ?? null
      );
    }
  }

  return (
    (clientVobsForPatient.find(
      (clientVob) =>
        clientVob.vobTypeId === 3 &&
        clientVob.isIntEnabled === true &&
        isClientVobVerifyEnabled(clientVob) &&
        clientVob.clientFacilityId == null
    ) as ClientVobVerifyFields | undefined) ?? null
  );
}

/** client_vob.is_verify_enabled — absent/undefined means show (default true). */
function isClientVobVerifyEnabled(
  clientVob: ClientVobStatusCardViewModel
): boolean {
  return (
    (clientVob as ClientVobVerifyFields).isVerifyEnabled !== false
  );
}

function getAvailityVOBErrorMessage(response: any): string {
  const message = response?.entity?.message;
  if (typeof message === 'string' && message.trim().length > 0) {
    return message;
  }
  return AvailityVOBAErrorMessages.GENERIC_ERROR;
}

function normalizeDob(raw: string | null | undefined): string {
  return formatAvailityDob(raw);
}

function hasDemographicsChanged(
  formValues: PatientFormValues,
  selectedPatient: {
    contact?: {
      firstName?: string;
      lastName?: string;
      birthDate?: string | null;
    };
  } | null
): boolean {
  const formDob = normalizeDob(formValues.dateOfBirth);
  const savedDob = normalizeDob(selectedPatient?.contact?.birthDate);
  return (
    (formValues.firstName || '').trim() !==
      (selectedPatient?.contact?.firstName || '').trim() ||
    (formValues.lastName || '').trim() !==
      (selectedPatient?.contact?.lastName || '').trim() ||
    formDob !== savedDob
  );
}

function hasInsuranceChanged(
  formValues: PatientFormValues,
  savedInsurance: {
    payorId?: number;
    payorPlanId?: number;
    memberId?: string;
    groupNumber?: string;
    subscriberName?: string;
    subscriberRelation?: number;
  } | null,
  insuranceOverride?: VerifyPayerInsuranceOverride | null
): boolean {
  const payorId = insuranceOverride?.payorId ?? Number(formValues.payorId);
  const payorPlanId =
    insuranceOverride?.payorPlanId ?? Number(formValues.payorPlanId);
  return (
    payorId !== Number(savedInsurance?.payorId || 0) ||
    payorPlanId !== Number(savedInsurance?.payorPlanId || 0) ||
    (formValues.memberId || '').trim() !== (savedInsurance?.memberId || '').trim() ||
    (formValues.groupNumber || '').trim() !==
      (savedInsurance?.groupNumber || '').trim() ||
    (formValues.subscriberName || '').trim() !==
      (savedInsurance?.subscriberName || '').trim() ||
    Number(formValues.subscriberRelation || 0) !==
      Number(savedInsurance?.subscriberRelation || 0)
  );
}

function resolvePlanForCandidate(
  candidate: VerifyPayerCandidate,
  selectedPayorId: number,
  selectedPlanId: number,
  payersMap: Map<number, PayerRow> | null | undefined
): { planId: number; planName?: string } {
  const masterPayor = payersMap?.get(candidate.payorId);
  if (candidate.payorId === selectedPayorId && selectedPlanId > 0) {
    const selectedPlan = masterPayor?.payorPlans?.find(
      (plan) => Number(plan.payorPlanId) === selectedPlanId
    );
    return {
      planId: selectedPlanId,
      planName: selectedPlan?.planName,
    };
  }
  const firstPlan = masterPayor?.payorPlans?.[0];
  return {
    planId: Number(firstPlan?.payorPlanId) || 0,
    planName: firstPlan?.planName,
  };
}

function findCignaCandidate(
  candidates: VerifyPayerCandidate[]
): VerifyPayerCandidate | undefined {
  const target = normalizePayorName('Cigna');
  return candidates.find((candidate) => {
    const name = normalizePayorName(candidate.payorName);
    return (
      name === target || name.startsWith(target) || name.includes(target)
    );
  });
}

function stampEntityForScore(
  entity: any,
  payorId: number,
  planId: number,
  payorName?: string,
  _planName?: string
) {
  // Availity GET /vob always resolves payor/plan from Coverages (or errors).
  // Prefer those ids; candidate stamp is only a safety net for older responses.
  // Do not attach planName — POST /vob does not accept it on plan.
  const serviceResolved =
    Number(entity?.payor?.payorId) > 0 && Number(entity?.plan?.payorPlanId) > 0;
  const effectivePayorId = serviceResolved
    ? Number(entity.payor.payorId)
    : payorId;
  const effectivePlanId = serviceResolved
    ? Number(entity.plan.payorPlanId)
    : planId;
  const effectivePayorName = serviceResolved
    ? entity.payor?.payorName || payorName
    : payorName;

  const { planName: _dropPlanName, ...planWithoutName } = entity?.plan ?? {};
  return {
    ...entity,
    payor: {
      ...(entity?.payor ?? {}),
      payorId: effectivePayorId,
      ...(effectivePayorName ? { payorName: effectivePayorName } : {}),
    },
    plan: {
      ...planWithoutName,
      payorPlanId: effectivePlanId,
    },
  };
}

function pickWinner(
  results: CandidateRunResult[],
  selectedPayorId: number
): CandidateRunResult | null {
  const completed = results.filter((r) => r.completed);
  if (completed.length === 0) {
    return null;
  }
  const selectedCompleted = completed.find(
    (r) => r.candidate.payorId === selectedPayorId
  );
  if (selectedCompleted) {
    return selectedCompleted;
  }
  return completed.reduce((best, current) => {
    if (current.compScore > best.compScore) {
      return current;
    }
    if (
      current.compScore === best.compScore &&
      current.candidate.payorId === selectedPayorId
    ) {
      return current;
    }
    return best;
  });
}

export function useRunAvailityVob(
  formValues: PatientFormValues,
  saveFormChanges: (
    insuranceOverride?: VerifyPayerInsuranceOverride,
    options?: SavePatientFormOptions
  ) => Promise<SavePatientFormResult>,
  /** Same validation as Save — used when Verify must create the patient first. */
  validateFormForCreate?: () => Promise<boolean>
) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const allClientsWithFacilitiesMap = useSelector(
    (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient
        .allClientsWithFacilitiesMap
  );
  const allClientsMap = useSelector(
    (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient.allClientsMap
  );
  const allClientVobs = useSelector(
    (state: RootState) =>
      state.implementationContext?.implementationSpecialistClient.allClientVobs
  );
  const payersMap = useSelector(
    (state: RootState) => state.adminContext.adminConfigurationContext?.payersMap
  );
  const vobSources = useSelector(
    (state: RootState) => state.metaData.vobSource
  );
  const selectedPatient = useSelector(
    (state: RootState) => state.patientContext.selectedPatient
  );
  const selectedPatientCurrentInsurance = useSelector(
    (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
  );

  const [isRunningAvailityVOB, setIsRunningAvailityVOB] = useState(false);
  const [showUpdateInsuranceModal, setShowUpdateInsuranceModal] = useState(false);
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);
  const [pendingVobId, setPendingVobId] = useState<number | null>(null);
  const [pendingInsuranceOverride, setPendingInsuranceOverride] =
    useState<VerifyPayerInsuranceOverride | null>(null);
  const [availityFacilities, setAvailityFacilities] = useState<FacilityWithNpi[]>(
    []
  );
  const [isLoadingAvailityFacilities, setIsLoadingAvailityFacilities] =
    useState(false);
  const [showFacilityPickerModal, setShowFacilityPickerModal] = useState(false);
  const abortControllersRef = useRef<AbortController[]>([]);

  const clientId = formValues.clientId ? Number(formValues.clientId) : 0;
  const patientId = Number(formValues.patientId) || 0;

  /** First Redux facility — used for Availity visibility (same as VOB default). */
  const visibilityFacilityId = useMemo(() => {
    if (!clientId) {
      return 0;
    }
    const client =
      allClientsWithFacilitiesMap?.get(clientId) ??
      allClientsMap?.get(clientId);
    const first = client?.clientFacilities?.[0];
    const id = Number(first?.facilityId);
    return Number.isInteger(id) && id > 0 ? id : 0;
  }, [clientId, allClientsWithFacilitiesMap, allClientsMap]);

  const clientFacilities = useMemo(() => {
    if (!clientId) {
      return [];
    }
    const client =
      allClientsWithFacilitiesMap?.get(clientId) ??
      allClientsMap?.get(clientId);
    return client?.clientFacilities ?? [];
  }, [clientId, allClientsWithFacilitiesMap, allClientsMap]);

  const showRunAvailityButton =
    clientId > 0 &&
    isAvailityVobConfigVisible(clientId, visibilityFacilityId, allClientVobs);

  const showAvailitySplitButton =
    !isLoadingAvailityFacilities && availityFacilities.length >= 2;

  const showInlineFacilityMenuItems =
    showAvailitySplitButton &&
    availityFacilities.length <= AVAILITY_FACILITY_MENU_MAX;

  useEffect(() => {
    if (!showRunAvailityButton) {
      setAvailityFacilities([]);
      setIsLoadingAvailityFacilities(false);
      return;
    }
    setIsLoadingAvailityFacilities(false);
    setAvailityFacilities(listFacilitiesWithValidNpi(clientFacilities));
  }, [showRunAvailityButton, clientFacilities]);

  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
      abortControllersRef.current = [];
    };
  }, []);

  const cancelAvailityVob = useCallback(() => {
    abortControllersRef.current.forEach((controller) => controller.abort());
  }, []);

  const navigateToSavedVob = useCallback(
    (vobId: number, forPatientId?: number) => {
      const id = forPatientId ?? patientId;
      navigate(`/patients/${id}/vob/${vobId}`);
    },
    [navigate, patientId]
  );

  const finishAfterInsurancePrompt = useCallback(
    async (shouldUpdate: boolean) => {
      if (pendingVobId == null || !patientId) {
        setShowUpdateInsuranceModal(false);
        setPendingVobId(null);
        setPendingInsuranceOverride(null);
        return;
      }

      if (shouldUpdate) {
        setIsSavingInsurance(true);
        try {
          const saved = await saveFormChanges(
            pendingInsuranceOverride ?? undefined
          );
          if (!saved.ok) {
            setIsSavingInsurance(false);
            return;
          }
        } catch (error) {
          console.error('Failed to update patient/insurance after VOB:', error);
          dispatch(showErrorStatus('Failed to update patient information'));
          setIsSavingInsurance(false);
          return;
        }
        setIsSavingInsurance(false);
      }

      const vobId = pendingVobId;
      setShowUpdateInsuranceModal(false);
      setPendingVobId(null);
      setPendingInsuranceOverride(null);
      navigateToSavedVob(vobId);
    },
    [
      dispatch,
      navigateToSavedVob,
      patientId,
      pendingInsuranceOverride,
      pendingVobId,
      saveFormChanges,
    ]
  );

  const onUpdateInsuranceYes = useCallback(() => {
    void finishAfterInsurancePrompt(true);
  }, [finishAfterInsurancePrompt]);

  const onUpdateInsuranceNo = useCallback(() => {
    void finishAfterInsurancePrompt(false);
  }, [finishAfterInsurancePrompt]);

  const openFacilityPicker = useCallback(() => {
    if (availityFacilities.length === 0) {
      dispatch(
        showErrorStatus('No client facility with a valid NPI was found')
      );
      return;
    }
    setShowFacilityPickerModal(true);
  }, [availityFacilities.length, dispatch]);

  const closeFacilityPicker = useCallback(() => {
    setShowFacilityPickerModal(false);
  }, []);

  const runAvailityVob = useCallback(
    async (facilityOverride?: FacilityWithNpi) => {
      const missingFields: string[] = [];
      const memberId = formValues.memberId || '';
      const dob = formatAvailityDob(formValues.dateOfBirth);

      if (!formValues.firstName?.trim()) {
        missingFields.push('First Name');
      }
      if (!formValues.lastName?.trim()) {
        missingFields.push('Last Name');
      }
      if (!dob) {
        missingFields.push('Date of Birth');
      }
      if (!formValues.payorId) {
        missingFields.push('Current Insurance Payer');
      }
      if (!formValues.payorPlanId) {
        missingFields.push('Current Insurance Plan');
      }
      if (!memberId.trim()) {
        missingFields.push('Member ID');
      }

      if (missingFields.length > 0) {
        dispatch(
          showErrorStatus(
            `The following fields are required to run Availity VOB: ${missingFields.join(', ')}`
          )
        );
        return;
      }

      const selectedPayorId = Number(formValues.payorId);
      const selectedPlanId = Number(formValues.payorPlanId);
      // Non-Availity selected payor is omitted from fan-out; configured payors still run.

      const facilityWithNpi =
        facilityOverride ??
        availityFacilities[0] ??
        null;
      if (!facilityWithNpi) {
        dispatch(
          showErrorStatus(
            'No client facility with a valid NPI was found'
          )
        );
        return;
      }

      const matchingClientVob = findMatchingVerifyClientVob(
        clientId,
        visibilityFacilityId,
        allClientVobs
      );
      const configuredPayorIds = matchingClientVob?.verifyPayorIds ?? [];

      const candidates = buildVerifyPayerCandidates(
        selectedPayorId,
        configuredPayorIds,
        payersMap,
        vobSources
      );
      if (candidates.length === 0) {
        dispatch(
          showErrorStatus(
            'No Availity payors available to verify (configured Verify payors and selected payer have no Availity codes)'
          )
        );
        return;
      }

      let effectivePatientId = patientId;
      if (!effectivePatientId) {
        const formValid = validateFormForCreate
          ? await validateFormForCreate()
          : false;
        if (!formValid) {
          return;
        }
      }

      abortControllersRef.current.forEach((controller) => controller.abort());
      const controllers = candidates.map(() => new AbortController());
      abortControllersRef.current = controllers;

      setIsRunningAvailityVOB(true);
      // When we temporarily seed Cigna so getAVVob accepts the call, restore the
      // form's original insurance if Verify fails (no completed winner).
      let restoreInsuranceOnFailure = false;
      try {
        if (!effectivePatientId) {
          const createResult = await saveFormChanges(undefined, {
            skipNavigateOnCreate: true,
          });
          if (!createResult.ok) {
            return;
          }
          effectivePatientId = createResult.patientId;
        }

        // TODO: Remove this Cigna seed once availity GET /vob can resolve config from
        // pcode alone. Today it still loads the patient's saved insurance payor and
        // requires an Availity payor_source for that payor before applying the pcode
        // override — so a non-Availity form payor fails even when fan-out pcodes are valid.
        // Workaround: temporarily save Cigna (Availity-configured) so the call proceeds;
        // restore original insurance on Verify failure (see restoreInsuranceOnFailure).
        const selectedPcode = getAvailityPcodeForPayor(
          selectedPayorId,
          payersMap,
          vobSources
        );
        if (!selectedPcode) {
          const cigna = findCignaCandidate(candidates);
          if (!cigna) {
            dispatch(
              showErrorStatus(
                'Cigna is not configured for Availity and is required when the selected payer is not an Availity payor'
              )
            );
            return;
          }
          const cignaPlan = resolvePlanForCandidate(
            cigna,
            selectedPayorId,
            selectedPlanId,
            payersMap
          );
          if (!cignaPlan.planId) {
            dispatch(
              showErrorStatus(
                'Cigna has no plan configured; cannot prepare patient for Availity VOB'
              )
            );
            return;
          }
          const saved = await saveFormChanges({
            payorId: cigna.payorId,
            payorPlanId: cignaPlan.planId,
          });
          if (!saved.ok) {
            return;
          }
          restoreInsuranceOnFailure = true;
        }

        const baseOverrides = {
          fname: formValues.firstName,
          lname: formValues.lastName,
          dob,
          memberId: formValues.memberId,
          npi: facilityWithNpi.npi,
        };

        const pending = candidates.map(async (candidate, index) => {
          const controller = controllers[index];
          const response = await getAvailityVobWithOverrides(
            effectivePatientId,
            facilityWithNpi.facilityId,
            { ...baseOverrides, pcode: candidate.pcode },
            controller.signal
          );
          const canceled = isAvailityRequestCanceled(
            response,
            controller.signal
          );
          const { planId, planName } = resolvePlanForCandidate(
            candidate,
            selectedPayorId,
            selectedPlanId,
            payersMap
          );
          const inactive =
            !canceled &&
            !response.hasErrors &&
            response.entity?.activePolicy === false;
          const completed =
            !canceled &&
            !response.hasErrors &&
            response.entity != null &&
            response.entity?.activePolicy !== false;
          const stamped = completed
            ? stampEntityForScore(
                response.entity,
                candidate.payorId,
                planId,
                candidate.payorName,
                planName
              )
            : null;
          return {
            candidate,
            response,
            canceled,
            completed,
            inactive,
            compScore: stamped ? calculateCompScore(stamped) : -1,
            planId,
            planName,
          } as CandidateRunResult;
        });

        // Prefer selected as soon as it completes; cancel the rest.
        const selectedIndex = candidates.findIndex(
          (c) => c.payorId === selectedPayorId
        );
        let results: CandidateRunResult[] = [];

        if (selectedIndex >= 0) {
          const selectedResult = await pending[selectedIndex];
          if (selectedResult.completed) {
            controllers.forEach((controller, i) => {
              if (i !== selectedIndex) {
                controller.abort();
              }
            });
            results = [selectedResult];
          } else {
            results = await Promise.all(pending);
          }
        } else {
          results = await Promise.all(pending);
        }

        const usableResults = results.filter((r) => !r.canceled);
        if (results.length > 0 && results.every((r) => r.canceled)) {
          return;
        }
        const winner = pickWinner(usableResults, selectedPayorId);

        if (!winner) {
          const selectedResult = usableResults.find(
            (r) => r.candidate.payorId === selectedPayorId
          );
          if (selectedResult?.inactive) {
            dispatch(
              showErrorStatus("Patient's insurance policy is not active")
            );
            return;
          }
          const anyInactive = usableResults.find((r) => r.inactive);
          if (anyInactive && usableResults.every((r) => r.inactive || r.response.hasErrors)) {
            dispatch(
              showErrorStatus("Patient's insurance policy is not active")
            );
            return;
          }
          const errorSource =
            selectedResult?.response ??
            usableResults.find((r) => r.response.hasErrors)?.response;
          dispatch(
            showErrorStatus(
              errorSource
                ? getAvailityVOBErrorMessage(errorSource)
                : AvailityVOBAErrorMessages.GENERIC_ERROR
            )
          );
          return;
        }

        const masterPayor = payersMap?.get(winner.candidate.payorId);
        const vobToSave = stampEntityForScore(
          winner.response.entity,
          winner.candidate.payorId,
          winner.planId,
          masterPayor?.payorName ?? winner.candidate.payorName,
          winner.planName
        );

        const response = await dispatch(createSelectedVob(vobToSave));
        if (response.payload) {
          // Successful VOB — keep any temporary seed; Save Changes decides persistence.
          restoreInsuranceOnFailure = false;
          await dispatch(loadPatientVobList(effectivePatientId));
          const vobId = (response.payload as { vobId: number }).vobId;
          const insuranceOverride: VerifyPayerInsuranceOverride = {
            payorId: Number(vobToSave.payor?.payorId) || winner.candidate.payorId,
            payorPlanId: Number(vobToSave.plan?.payorPlanId) || winner.planId,
          };
          const demographicsChanged = hasDemographicsChanged(
            formValues,
            selectedPatient
          );
          const insuranceChanged = hasInsuranceChanged(
            formValues,
            selectedPatientCurrentInsurance,
            insuranceOverride
          );

          if (!demographicsChanged && !insuranceChanged) {
            navigateToSavedVob(vobId, effectivePatientId);
            return;
          }

          setPendingVobId(vobId);
          setPendingInsuranceOverride(insuranceOverride);
          setShowUpdateInsuranceModal(true);
        }
      } catch (error) {
        if (controllers.every((controller) => controller.signal.aborted)) {
          return;
        }
        console.error('Failed to run Availity VOB:', error);
        dispatch(showErrorStatus('Failed to run Availity VOB'));
      } finally {
        if (restoreInsuranceOnFailure) {
          try {
            await saveFormChanges({
              payorId: selectedPayorId,
              payorPlanId: selectedPlanId,
            });
          } catch (restoreError) {
            console.error(
              'Failed to restore patient insurance after Verify error:',
              restoreError
            );
          }
        }
        abortControllersRef.current = [];
        setIsRunningAvailityVOB(false);
      }
    },
    [
      allClientVobs,
      availityFacilities,
      clientId,
      dispatch,
      formValues,
      navigateToSavedVob,
      patientId,
      payersMap,
      saveFormChanges,
      selectedPatient,
      selectedPatientCurrentInsurance,
      validateFormForCreate,
      visibilityFacilityId,
      vobSources,
    ]
  );

  const confirmFacilityPicker = useCallback(
    (facility: FacilityWithNpi) => {
      setShowFacilityPickerModal(false);
      void runAvailityVob(facility);
    },
    [runAvailityVob]
  );

  return {
    showRunAvailityButton,
    showAvailitySplitButton,
    showInlineFacilityMenuItems,
    availityFacilities,
    isLoadingAvailityFacilities,
    isRunningAvailityVOB,
    cancelAvailityVob,
    runAvailityVob,
    showUpdateInsuranceModal,
    isSavingInsurance,
    onUpdateInsuranceYes,
    onUpdateInsuranceNo,
    showFacilityPickerModal,
    openFacilityPicker,
    closeFacilityPicker,
    confirmFacilityPicker,
  };
}
